import React, { useState } from "react";
import { vendorService } from "./vendor.service";
import { VendorFormData } from "./vendor.types";

const VendorModal = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone_number: "",
    company_store_name: "",
    message: "",
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else if (!/^[0-9+\-\s()]{10,20}$/.test(formData.phone_number)) {
      newErrors.phone_number = "Invalid phone number format";
    }

    if (!formData.company_store_name.trim()) {
      newErrors.company_store_name = "Company/Store name is required";
    } else if (formData.company_store_name.trim().length < 2) {
      newErrors.company_store_name = "Company/Store name must be at least 2 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setSuccessMessage("");
    setErrorMessage("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = new VendorFormData(
        formData.name,
        formData.phone_number,
        formData.company_store_name,
        formData.message
      );

      const response = await vendorService.registerVendor(payload);

      if (response.status) {
        setSuccessMessage(response.data.message);
        
        // Reset form
        setFormData({
          name: "",
          phone_number: "",
          company_store_name: "",
          message: "",
        });

        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
          setSuccessMessage("");
        }, 2000);
      } else {
        setErrorMessage(response.data.error);
      }
    } catch (error) {
      setErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: "",
        phone_number: "",
        company_store_name: "",
        message: "",
      });
      setErrors({});
      setSuccessMessage("");
      setErrorMessage("");
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[99999] px-4">
      <div className="relative w-full max-w-[854px] p-8 bg-white rounded-lg shadow-xl">
        {/* Close Button */}
        <button
          onClick={handleClose}
          disabled={loading}
          className="absolute text-lg text-gray-500 top-6 right-7 hover:text-black disabled:opacity-50"
        >
          ✕
        </button>

        {/* Title */}
        <h2 className="text-[32px] font-semibold text-[#382C28]">
          Register As Vendor Now
        </h2>

        <p className="mt-4 mb-6 text-[18px] text-[#776E6B]">
          So our team can reach out to you on time
        </p>

        {/* Success Message */}
        {successMessage && (
          <div className="p-4 mb-4 text-green-800 bg-green-100 border border-green-200 rounded-md">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="p-4 mb-4 text-red-800 bg-red-100 border border-red-200 rounded-md">
            {errorMessage}
          </div>
        )}

        {/* Form */}
        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Name + Phone */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col">
              <label className="mb-2 text-sm text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Name"
                disabled={loading}
                className={`px-4 py-2 border rounded-md outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.name && (
                <span className="mt-1 text-sm text-red-500">{errors.name}</span>
              )}
            </div>

            <div className="flex flex-col">
              <label className="mb-2 text-sm text-gray-700">
                Phone number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="Phone Number"
                disabled={loading}
                className={`px-4 py-2 border rounded-md outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  errors.phone_number ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.phone_number && (
                <span className="mt-1 text-sm text-red-500">
                  {errors.phone_number}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col">
            <label className="mb-2 text-sm text-gray-700">
              Company/Store Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="company_store_name"
              value={formData.company_store_name}
              onChange={handleChange}
              placeholder="Company/Store Name"
              disabled={loading}
              className={`px-4 py-2 border rounded-md outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                errors.company_store_name ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.company_store_name && (
              <span className="mt-1 text-sm text-red-500">
                {errors.company_store_name}
              </span>
            )}
          </div>

          {/* Message */}
          <div className="flex flex-col">
            <label className="mb-2 text-sm text-gray-700">Message</label>
            <textarea
              rows={4}
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Message content..."
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md outline-none resize-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            ></textarea>
          </div>

          {/* Button */}
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3 bg-[#6a0dad] hover:bg-[#6b1fb0] text-white font-semibold tracking-wider rounded-[8px] shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "SENDING..." : "SEND MESSAGE"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default VendorModal;